/**
 * `localStorage` is not guaranteed to exist. In restricted WebViews (blocked
 * site data, private browsing, some Android OEM privacy modes) the *accessor*
 * throws, not just the read — so `typeof localStorage !== 'undefined'` is not
 * a sufficient guard and a bare `localStorage.getItem` can take down module
 * evaluation.
 *
 * Every helper here degrades to a no-op instead of propagating.
 */

function storage (): Storage | null {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

export const safeStorage = {
  /** Reads a key, returning `fallback` (default `null`) if storage is unusable. */
  get (key: string, fallback: string | null = null): string | null {
    try {
      return storage()?.getItem(key) ?? fallback
    } catch {
      return fallback
    }
  },

  /** Writes a key. Silently ignores quota and permission failures. */
  set (key: string, value: string): void {
    try {
      storage()?.setItem(key, value)
    } catch {
      /* storage unavailable or full — the caller must not depend on persistence */
    }
  },

  /** Removes a key. Silently ignores permission failures. */
  remove (key: string): void {
    try {
      storage()?.removeItem(key)
    } catch {
      /* storage unavailable — nothing to remove */
    }
  },

  /** True when reads and writes are expected to work. */
  isAvailable (): boolean {
    try {
      const s = storage()
      if (!s) return false
      const probe = '__rs_probe__'
      s.setItem(probe, '1')
      s.removeItem(probe)
      return true
    } catch {
      return false
    }
  }
}
